<template>
    <PageLayout title="Dashboard">
        <template #actions>
            <div class="page-date-filter dashboard-date-filter">
                <v-date-picker v-model="filter.date" mode="date" :max-date="new Date()">
                    <template v-slot="{ inputValue, togglePopover }">
                        <button type="button" class="btn btn-outline-primary dashboard-date-btn" @click="togglePopover()">
                            <i class="fas fa-calendar-alt mr-2"></i>
                            {{ inputValue || 'Hari ini' }}
                        </button>
                    </template>
                </v-date-picker>
            </div>
        </template>

        <div v-if="initialLoading" class="dashboard-loading">
            <div class="spinner-border text-primary" role="status"></div>
        </div>

        <template v-else>
            <div class="row">
                <div class="col-md-6 col-xl mb-30" v-for="card in statCards" :key="card.key">
                    <div class="dashboard-stat-card" :class="'dashboard-stat-card--' + card.tone">
                        <div class="dashboard-stat-card__icon">
                            <i :class="card.icon"></i>
                        </div>
                        <div class="dashboard-stat-card__body">
                            <div class="dashboard-stat-card__value">{{ card.value }}</div>
                            <div class="dashboard-stat-card__label">{{ card.label }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-lg-8 mb-30">
                    <ContentCard card-class="dashboard-chart-card">
                        <div class="dashboard-card-header">
                            <h5 class="dashboard-card-title">Aktivitas 7 Hari Terakhir</h5>
                            <span class="dashboard-card-subtitle">Pesan keluar & masuk per hari</span>
                        </div>
                        <div class="dashboard-chart-wrap dashboard-chart-wrap--line">
                            <canvas ref="lineChartCanvas" />
                        </div>
                    </ContentCard>
                </div>
                <div class="col-lg-4 mb-30">
                    <ContentCard card-class="dashboard-chart-card">
                        <div class="dashboard-card-header">
                            <h5 class="dashboard-card-title">Distribusi Pesan</h5>
                            <span class="dashboard-card-subtitle">Ringkasan hari terpilih</span>
                        </div>
                        <div class="dashboard-chart-wrap dashboard-chart-wrap--doughnut">
                            <canvas ref="doughnutChartCanvas" />
                        </div>
                        <div class="dashboard-legend">
                            <div class="dashboard-legend__item">
                                <span class="dashboard-legend__dot dashboard-legend__dot--success"></span>
                                Terkirim {{ formatNumber(statistics.message_sent) }}
                            </div>
                            <div class="dashboard-legend__item">
                                <span class="dashboard-legend__dot dashboard-legend__dot--warning"></span>
                                Pending {{ formatNumber(statistics.message_pending) }}
                            </div>
                            <div class="dashboard-legend__item">
                                <span class="dashboard-legend__dot dashboard-legend__dot--danger"></span>
                                Gagal {{ formatNumber(statistics.message_failed) }}
                            </div>
                        </div>
                    </ContentCard>
                </div>
            </div>
        </template>
    </PageLayout>
</template>

<script>
    import moment from 'moment'
    import {
        Chart,
        Title,
        Tooltip,
        Legend,
        ArcElement,
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        LineController,
        DoughnutController,
        Filler,
    } from 'chart.js'

    Chart.register(
        Title,
        Tooltip,
        Legend,
        ArcElement,
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        LineController,
        DoughnutController,
        Filler
    )

    export default {
        data() {
            return {
                initialLoading: true,
                filter: {
                    date: new Date(),
                },
                statistics: {
                    message_sent: 0,
                    message_pending: 0,
                    message_failed: 0,
                    incoming_message: 0,
                    total_outgoing: 0,
                    success_rate: 0,
                    queued: 0,
                },
                chart: [],
                lineChart: null,
                doughnutChart: null,
            }
        },
        computed: {
            statCards() {
                return [
                    {
                        key: 'sent',
                        label: 'Pesan Terkirim',
                        value: this.formatNumber(this.statistics.message_sent),
                        icon: 'fas fa-paper-plane',
                        tone: 'success',
                    },
                    {
                        key: 'failed',
                        label: 'Pesan Gagal',
                        value: this.formatNumber(this.statistics.message_failed),
                        icon: 'fas fa-times-circle',
                        tone: 'danger',
                    },
                    {
                        key: 'pending',
                        label: 'Pesan Pending',
                        value: this.formatNumber(this.statistics.message_pending),
                        icon: 'fas fa-clock',
                        tone: 'warning',
                    },
                    {
                        key: 'incoming',
                        label: 'Pesan Masuk',
                        value: this.formatNumber(this.statistics.incoming_message),
                        icon: 'fas fa-inbox',
                        tone: 'info',
                    },
                    {
                        key: 'rate',
                        label: 'Success Rate',
                        value: `${this.statistics.success_rate.toFixed(1)}%`,
                        icon: 'fas fa-chart-line',
                        tone: 'primary',
                    },
                ]
            },
            chartPoints() {
                if (this.chart.length) {
                    return this.chart
                }

                const endDate = this.filter.date ? moment(this.filter.date) : moment()
                const points = []

                for (let i = 6; i >= 0; i--) {
                    const day = endDate.clone().subtract(i, 'days')
                    points.push({
                        label: day.format('DD/MM'),
                        sent: 0,
                        failed: 0,
                        incoming: 0,
                    })
                }

                return points
            },
            lineChartMaxValue() {
                return this.chartPoints.reduce((max, item) => {
                    return Math.max(max, item.sent || 0, item.failed || 0, item.incoming || 0)
                }, 0)
            },
            lineChartData() {
                return {
                    labels: this.chartPoints.map((item) => item.label),
                    datasets: [
                        {
                            label: 'Terkirim',
                            data: this.chartPoints.map((item) => item.sent || 0),
                            borderColor: '#22c6ab',
                            backgroundColor: 'rgba(34, 198, 171, 0.12)',
                            fill: true,
                            tension: 0.35,
                            pointRadius: 4,
                            pointHoverRadius: 6,
                            pointBackgroundColor: '#22c6ab',
                            borderWidth: 2,
                        },
                        {
                            label: 'Gagal',
                            data: this.chartPoints.map((item) => item.failed || 0),
                            borderColor: '#ef6e6e',
                            backgroundColor: 'rgba(239, 110, 110, 0.08)',
                            fill: true,
                            tension: 0.35,
                            pointRadius: 4,
                            pointHoverRadius: 6,
                            pointBackgroundColor: '#ef6e6e',
                            borderWidth: 2,
                        },
                        {
                            label: 'Masuk',
                            data: this.chartPoints.map((item) => item.incoming || 0),
                            borderColor: '#4798e8',
                            backgroundColor: 'rgba(71, 152, 232, 0.08)',
                            fill: true,
                            tension: 0.35,
                            pointRadius: 4,
                            pointHoverRadius: 6,
                            pointBackgroundColor: '#4798e8',
                            borderWidth: 2,
                        },
                    ],
                }
            },
            lineChartOptions() {
                return {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                        legend: {
                            position: 'bottom',
                            labels: { boxWidth: 12, padding: 16 },
                        },
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            ticks: { color: '#6c757d', maxRotation: 0 },
                        },
                        y: {
                            beginAtZero: true,
                            min: 0,
                            suggestedMax: Math.max(5, this.lineChartMaxValue + 1),
                            ticks: {
                                precision: 0,
                                stepSize: 1,
                                color: '#6c757d',
                            },
                            grid: { color: 'rgba(0, 0, 0, 0.08)' },
                            border: { display: false },
                        },
                    },
                }
            },
            doughnutChartData() {
                const sent = this.statistics.message_sent || 0
                const pending = this.statistics.message_pending || 0
                const failed = this.statistics.message_failed || 0
                const total = sent + pending + failed

                if (total > 0) {
                    return {
                        labels: ['Terkirim', 'Pending', 'Gagal'],
                        datasets: [{
                            backgroundColor: ['#22c6ab', '#ffbc34', '#ef6e6e'],
                            borderWidth: 0,
                            data: [sent, pending, failed],
                        }],
                    }
                }

                return {
                    labels: ['Belum ada data'],
                    datasets: [{
                        backgroundColor: ['#e3e6ef'],
                        borderWidth: 0,
                        data: [1],
                    }],
                }
            },
            doughnutChartOptions() {
                return {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '68%',
                    plugins: {
                        legend: { display: false },
                    },
                }
            },
        },
        watch: {
            'filter.date': {
                handler() {
                    this.fetchStatistics()
                },
            },
        },
        mounted() {
            this.fetchStatistics()
        },
        beforeDestroy() {
            this.destroyCharts()
        },
        methods: {
            async fetchStatistics() {
                try {
                    const date = this.filter.date
                        ? moment(this.filter.date).format('YYYY-MM-DD')
                        : moment().format('YYYY-MM-DD')

                    const response = await this.$store.dispatch('user_statistics', { date })
                    const data = response.data.data

                    this.statistics = data.statistics || this.statistics
                    this.chart = data.chart || []
                } catch (err) {
                    this.$toast.error('Gagal memuat statistik dashboard')
                    this.chart = []
                } finally {
                    this.initialLoading = false
                    await this.$nextTick()
                    this.renderCharts()
                }
            },
            renderCharts() {
                this.renderLineChart()
                this.renderDoughnutChart()
            },
            renderLineChart() {
                const canvas = this.$refs.lineChartCanvas
                if (!canvas) return

                if (this.lineChart) {
                    this.lineChart.data = this.lineChartData
                    this.lineChart.options = this.lineChartOptions
                    this.lineChart.update()
                    return
                }

                this.lineChart = new Chart(canvas.getContext('2d'), {
                    type: 'line',
                    data: this.lineChartData,
                    options: this.lineChartOptions,
                })
            },
            renderDoughnutChart() {
                const canvas = this.$refs.doughnutChartCanvas
                if (!canvas) return

                if (this.doughnutChart) {
                    this.doughnutChart.data = this.doughnutChartData
                    this.doughnutChart.options = this.doughnutChartOptions
                    this.doughnutChart.update()
                    return
                }

                this.doughnutChart = new Chart(canvas.getContext('2d'), {
                    type: 'doughnut',
                    data: this.doughnutChartData,
                    options: this.doughnutChartOptions,
                })
            },
            destroyCharts() {
                if (this.lineChart) {
                    this.lineChart.destroy()
                    this.lineChart = null
                }
                if (this.doughnutChart) {
                    this.doughnutChart.destroy()
                    this.doughnutChart = null
                }
            },
            formatNumber(value) {
                return new Intl.NumberFormat('id-ID').format(value || 0)
            },
        },
    }
</script>
